import { RefObject, useState } from 'react'
import { Box, Image, Link } from 'theme-ui'

import MenuDropdown from 'src/components/common/menuDropdown/MenuDropdown'
import BaseButton from 'src/components/common/buttons/BaseButton'
import Type from 'src/components/gel/Type'
import { scrollToRef } from 'src/lib/window'
import { defaultClickEvent } from 'src/lib/analytics'
import { useHeaderData } from 'src/lib/react-query/queries'

import type { HeaderData } from 'src/components/navigation/header/headerHelpers'

const DesktopNav = ({
  formRef,
  handleClick,
  headerData
}: {
  formRef?: RefObject<HTMLIFrameElement | null>
  handleClick: (
    event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
    formRef?: RefObject<HTMLIFrameElement | null>
  ) => void
  headerData: HeaderData | []
}) => {
  return (
    <Box
      as="nav"
      sx={{ display: ['none', 'none', 'none', 'none', 'none', 'flex', 'flex', 'flex'] }}
    >
      {headerData.map(headerItem =>
        Array.isArray(headerItem.items) ? (
          headerItem.items.length > 1 ? (
            <MenuDropdown
              key={headerItem.label}
              headerItem={{ label: headerItem.label, items: headerItem.items }}
            />
          ) : (
            <Box key={headerItem.label} sx={{ pr: 2, pt: 2 }}>
              <Link variant="navigation" href={headerItem.items?.[0].url}>
                <Type variant="Labels" as="p">
                  {headerItem.label}
                </Type>
              </Link>
            </Box>
          )
        ) : (
          <Box key={headerItem.label} sx={{ pr: 2, pt: 2 }}>
            <Link variant="navigation" href={headerItem.url} key={headerItem.label}>
              <Type variant="Labels" as="p">
                {headerItem.label}
              </Type>
            </Link>
          </Box>
        )
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', pl: 4 }}>
        <BaseButton
          variant="buttons.primary"
          onClick={e => {
            handleClick(e, formRef)
          }}
          text="Contact us"
          link="/#contact-form"
        />
      </Box>
    </Box>
  )
}

const MobileNav = ({
  formRef,
  handleClick,
  headerData
}: {
  formRef?: RefObject<HTMLIFrameElement | null>
  handleClick: (
    event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
    formRef?: RefObject<HTMLIFrameElement | null>
  ) => void
  headerData: HeaderData
}) => {
  const [toggleMobileOpen, setToggleMobileOpen] = useState(false)
  return (
    <Box
      sx={{
        display: ['block', 'block', 'block', 'block', 'block', 'none', 'none', 'none']
      }}
      aria-label="Mobile menu"
      role="navigation"
    >
      <BaseButton
        variant="buttons.tertiary"
        onClick={() => {
          defaultClickEvent({
            event_grouping: 'header',
            item_name: `Mobile menu ${toggleMobileOpen ? 'close' : 'open'}`,
            item_type: 'button',
            item_link: '#mobileMenu'
          })
          setToggleMobileOpen(!toggleMobileOpen)
        }}
        rightIcon={toggleMobileOpen ? 'GelIconClose' : 'GelIconListViewText'}
        name={toggleMobileOpen ? 'Close menu' : 'Open menu'}
      />
      {toggleMobileOpen ? (
        <Box
          sx={{
            width: '100%',
            position: 'absolute',
            left: '0',
            top: '56px',
            backgroundColor: 'background.quaternary',
            borderBottom: 'tertiaryThin',
            borderTop: 'tertiaryThin'
          }}
        >
          {headerData.map(headerItem =>
            Array.isArray(headerItem.items) && headerItem.items.length > 0 ? (
              headerItem.items.length > 1 ? (
                <MenuDropdown
                  key={headerItem.label}
                  headerItem={{ label: headerItem.label, items: headerItem.items }}
                />
              ) : (
                <Link variant="navigation" href={headerItem.items?.[0].url} key={headerItem.label}>
                  <Type variant="Labels" as="p">
                    {headerItem.label}
                  </Type>
                </Link>
              )
            ) : (
              <Link key={headerItem.label} href={headerItem.url} variant="navigation">
                <Type variant="Labels" as="p">
                  {headerItem.label}
                </Type>
              </Link>
            )
          )}
          <BaseButton
            text="Contact us"
            variant="buttons.navigation"
            onClick={e => {
              defaultClickEvent({
                event_grouping: 'header',
                item_name: 'Contact us - mobile',
                item_type: 'link',
                item_link: '#contactForm'
              })
              handleClick(e, formRef)
            }}
            link="/#contact-form"
          />
        </Box>
      ) : null}
    </Box>
  )
}

const Header = ({
  headerData: initialHeaderData,
  formRef
}: {
  headerData?: HeaderData
  formRef?: RefObject<HTMLIFrameElement | null>
}) => {
  const { data: headerDataQuery } = useHeaderData()

  const headerData = (headerDataQuery ? headerDataQuery : initialHeaderData || []).concat([
    { label: 'Ad Formats', url: '/ad-formats' }
  ])

  const handleClickScroll = (
    event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
    formRef?: RefObject<HTMLIFrameElement | null>
  ) => {
    if (formRef?.current) {
      event.preventDefault()
      defaultClickEvent({
        event_grouping: 'header',
        item_name: 'Contact us',
        item_type: 'button',
        item_link: '#contactForm'
      })
      scrollToRef(formRef)
    }
  }
  return (
    <Box
      as="header"
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        pt: [1, null, null, null, 0],
        pb: [1, null, null, null, 0],
        px: [2, null, 4],
        position: 'sticky',
        top: 0,
        zIndex: 'header',
        backgroundColor: 'background.quaternary',
        minHeight: ['48px', null, null, null, '72px'],
        borderBottom: 'tertiaryThin'
      }}
    >
      <Link
        sx={{
          display: 'flex',
          alignItems: 'center'
        }}
        href="/"
        className="tabFocusBlue"
      >
        <Image
          src="/images/BBC_STUDIOS_ADVERTISING_SingleLine.svg"
          alt="BBC Studios logo"
          sx={{
            height: ['16px', null, null, null, '20px', '24px'],
            width: 'auto'
          }}
        />
      </Link>
      <DesktopNav handleClick={handleClickScroll} formRef={formRef} headerData={headerData} />
      <MobileNav handleClick={handleClickScroll} formRef={formRef} headerData={headerData} />
    </Box>
  )
}

export default Header
